import { useEffect, useState } from 'react';
import { Card, TableContainer, TablePagination } from '@mui/material';
import HttpServices from 'src/services/httpService';
import { crmModuleURL } from 'src/services/urlService';
import { useToaster } from 'src/utils/toaster/toasterContext';
import Spinner from 'src/utils/Loader/spinner';
import NoPostFound from 'src/pages/publishedPosts/noPost.component';
import TriggersTable from './TriggersTable';
import EditTriggerDialog from './EditTriggerDialog';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { useTranslation } from 'react-i18next';

const AutomationTriggers = ({isShowOfferBanner=false }) => {
  const [automationTriggers, setAutomationTriggers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [height] = useState(window.innerHeight - 250);
  const [loader, setLoader] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [triggerToEdit, setTriggerToEdit] = useState(null);
  const { showToast } = useToaster();
  const { t: translate } = useTranslation();

  useEffect(() => {
    getAllAutomationTriggers();
  }, []);

  const getAllAutomationTriggers = () => {
    setLoader(true);
    HttpServices.Get({
      url: `${crmModuleURL.fetchAutomationTrigger}?type=comment`,
      successCallback: (data) => {
        setAutomationTriggers(data || []);
        setLoader(false);
      },
      failureCallback: (message) => {
        setLoader(false);
        showToast(message, 'e');
      },
    });
  };

  const handleDeleteAccount = () => {
    HttpServices.Post({
      url: crmModuleURL.deleteAutomationTrigger,
      body: { id: accountToDelete?.id },
      successCallback: () => {
        showToast(translate('automation_triggers.trigger_deleted', 'Trigger deleted successfully'), 's');
        setConfirmOpen(false);
        getAllAutomationTriggers();
      },
      failureCallback: (message) => {
        showToast(message, 'e');
        setConfirmOpen(false);
      },
    });
  };

  const handleAccountStatus = (row) => {
    const url = row.is_enabled ? crmModuleURL.disableAutomationTrigger : crmModuleURL.enableAutomationTrigger;
    HttpServices.Post({
      url,
      body: { id: row?.id },
      successCallback: () => {
        showToast(translate('automation_triggers.trigger_status_updated', 'Trigger status updated successfully'), 's');
        getAllAutomationTriggers();
      },
      failureCallback: (message) => {
        showToast(message, 'e');
      },
    });
  };

  const handleUpdateTrigger = (updatedTrigger) => {
    HttpServices.Post({
      url: crmModuleURL.updateAutomationTrigger,
      body: {
        id: updatedTrigger.id,
        webhook_profile_id: updatedTrigger.webhook_profile_id,
        trigger_type: updatedTrigger.trigger_type,
        trigger_values: updatedTrigger.any_trigger_value ? [] : updatedTrigger.trigger_values,
        any_trigger_value: updatedTrigger.any_trigger_value,
        dm_message: updatedTrigger.dm_message || '',
        comment_reply: updatedTrigger.comment_reply || '',
        dm_template: updatedTrigger.is_dm_template ? updatedTrigger.dm_template : null,
        is_dm_template: updatedTrigger.is_dm_template || false,
        allPost: updatedTrigger.allPost || false,
      },
      successCallback: () => {
        showToast(translate('automation_triggers.trigger_updated', 'Trigger updated successfully'), 's');
        setEditOpen(false);
        getAllAutomationTriggers();
      },
      failureCallback: (message) => {
        showToast(message, 'e');
      },
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <>
      {loader ? (
        <Spinner />
      ) : (
        <Card sx={{ m: 1, borderRadius: 2, boxShadow: 3 }}>
          {automationTriggers.length > 0 ? (
            <>
              <TableContainer sx={{ height: isShowOfferBanner ? 'calc(100vh - 250px)' : 'calc(100vh - 200px)' }}>
                <TriggersTable
                  triggers={automationTriggers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                  type={'comment'}
                  onStatusChange={handleAccountStatus}
                  onDelete={(row) => {
                    setAccountToDelete(row);
                    setConfirmOpen(true);
                  }}
                  onEdit={(row) => {
                    setTriggerToEdit(row);
                    setEditOpen(true);
                  }}
                />
              </TableContainer>
              <TablePagination
                component="div"
                rowsPerPageOptions={[10, 15, 25]}
                count={automationTriggers.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </>
          ) : (
            <NoPostFound height={height} contentText={translate('automation_triggers.triggers_not_found', 'Triggers Not found')} />
          )}
          <ConfirmationDialog
            open={confirmOpen}
            onClose={() => setConfirmOpen(false)}
            onConfirm={handleDeleteAccount}
            title={translate('automation_triggers.delete_title', 'Delete Automation Trigger')}
            message={translate('automation_triggers.delete_message', 'Are you sure you want to delete this automation trigger?')}
          />
          <EditTriggerDialog
            open={editOpen}
            onClose={() => setEditOpen(false)}
            trigger={triggerToEdit}
            type={'comment'}
            onSave={handleUpdateTrigger}
          />
        </Card>
      )}
    </>
  );
};

export default AutomationTriggers;